import { Flex, Spacer } from '@chakra-ui/react';
import React from 'react';

import { CreditAgreement } from '@finley-technologies/finley-types';

import { ConcentrationLimitInsightsGqlDataGQLType } from '../../../../../types/dashboard-admin-gqlTypes.generated';
import {
  generateConcentrationLimitChartConfig,
  getConcentrationLabelFormat,
} from './portfolioBorrowingBaseChartConfigs';
import { PortfolioConcentrationLimitsChartTemplate } from './PortfolioConcentrationLimitsChartTemplate';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  ChartTimePeriod,
  ConcentrationLimitFormatMap,
  onSelectDateRange,
} from '~/packages/portfolio/ui/shared/portfolioChartUtils';
import { PortfolioDownloadCsvLink } from '~/packages/portfolio/ui/shared/PortfolioDownloadCsvLink';
import { PortfolioTimePeriodSelectButtons } from '~/packages/portfolio/ui/shared/PortfolioTimePeriodSelectButtons';

type ConcentrationLimitsSectionProps = {
  creditFacilityId: string;
  chartDataDateRange: {
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  };
  setChartDataDateRange: React.Dispatch<
    React.SetStateAction<{
      chartDataStartDate: Date;
      chartDataEndDate: Date;
    }>
  >;
  selectedTimePeriod: ChartTimePeriod;
  onSelectTimePeriod: (timePeriod: ChartTimePeriod) => void;
  refetchConcentrationInsightsData: () => Promise<any>;
  concentrationLimitFormatMap: ConcentrationLimitFormatMap;
  concentrationLimitChartData: Map<
    string,
    ConcentrationLimitInsightsGqlDataGQLType[]
  >;
  creditAgreementConcentrationLimits: CreditAgreement.DomainEntity.ConcentrationLimitProperty[];
  limitKeyToTitleMap: Map<string, string>;
  combinedCsvData: {
    dataRows: any[];
    headers: { label: string; key: string }[];
  };
  respectConcentrationLimitFormat: boolean;
};

/**
 * Shared component for rendering concentration limits section with controls and charts
 * Used in both PortfolioBorrowingBaseContainer and ConcentrationContainerV2
 */
export const ConcentrationLimitsSection = ({
  creditFacilityId,
  chartDataDateRange,
  setChartDataDateRange,
  selectedTimePeriod,
  onSelectTimePeriod,
  refetchConcentrationInsightsData,
  concentrationLimitFormatMap,
  concentrationLimitChartData,
  creditAgreementConcentrationLimits,
  limitKeyToTitleMap,
  combinedCsvData,
  respectConcentrationLimitFormat,
}: ConcentrationLimitsSectionProps) => {
  if (concentrationLimitFormatMap.size === 0) {
    return null;
  }

  return (
    <Flex direction="column" gap="1rem">
      <Typography variant="section-title" trim>
        Concentration Limits
      </Typography>

      {/* Date Range and Time Period Controls */}
      <Flex alignItems="center" gap="1rem" mb="1rem">
        <FullDateRangePicker
          onSelectDateRange={(newDateRange) =>
            onSelectDateRange({
              newDateRange,
              refetch: refetchConcentrationInsightsData,
              creditFacilityId,
              setChartDataDateRange,
            })
          }
          currentDateRange={{
            startDate: chartDataDateRange.chartDataStartDate,
            endDate: chartDataDateRange.chartDataEndDate,
          }}
          minWidth="300px"
        />
        <PortfolioTimePeriodSelectButtons
          currentSelectedTimePeriod={selectedTimePeriod}
          onSelectTimePeriod={onSelectTimePeriod}
        />
        <Spacer />
        <PortfolioDownloadCsvLink
          dataRows={combinedCsvData.dataRows}
          headers={combinedCsvData.headers}
          chartTitle="All Concentration Limits"
          selectedTimePeriod={selectedTimePeriod}
        />
      </Flex>

      {/* Individual Concentration Limit Charts */}
      {Array.from(concentrationLimitFormatMap).map(
        ([concentrationLimitKey, valueType]) => {
          const creditAgreementConcentrationLimit = creditAgreementConcentrationLimits.find(
            (concentrationLimit) =>
              concentrationLimit._config_.concentrationLimitConfig?.propertyKey.toLowerCase() ===
              concentrationLimitKey.toLowerCase(),
          );

          const chartTitle =
            limitKeyToTitleMap.get(concentrationLimitKey) ||
            concentrationLimitKey;

          const concentrationValueFormat = getConcentrationLabelFormat(
            creditAgreementConcentrationLimit,
            respectConcentrationLimitFormat,
          );

          const chartDataForLimit =
            concentrationLimitChartData.get(concentrationLimitKey) || [];

          return (
            <PortfolioConcentrationLimitsChartTemplate
              creditFacilityId={creditFacilityId}
              chartTitle={chartTitle}
              chartConfig={generateConcentrationLimitChartConfig(
                valueType === 'number' ? 'float' : valueType,
                concentrationValueFormat,
              )}
              concentrationLimitKey={concentrationLimitKey}
              chartData={chartDataForLimit}
              selectedTimePeriod={selectedTimePeriod}
              key={concentrationLimitKey}
            />
          );
        },
      )}
    </Flex>
  );
};
